// @flow

import React from 'react'
import styled from 'styled-components'
import { Input, Select } from 'antd'
import { mediaMin } from 'frontend_core/dist/styles/media'
import Colors from 'components/styles/colors'
import { spacing, fontSize } from 'frontend_core/dist/styles/variables'
import { type ReservationType } from 'types/reservation'
import SelectItem from 'components/atoms/SelectItem'
import FormItem from 'components/molecules/FormItem'
import FormSectionTitle from 'components/molecules/FormSectionTitle'
import InputContainer from 'components/molecules/InputContainer'

export type PropTypes = {
  firstName: {
    value: string,
    errorTexts?: Array<string>,
    onChange: Function,
  },
  lastName: {
    value: string,
    errorTexts?: Array<string>,
    onChange: Function,
  },
  fullName: {
    errorTexts?: Array<string>,
  },
  firstNameKana: {
    value: string,
    errorTexts?: Array<string>,
    onChange: Function,
  },
  lastNameKana: {
    value: string,
    errorTexts?: Array<string>,
    onChange: Function,
  },
  fullNameKana: {
    errorTexts?: Array<string>,
  },
  paidMailAddress: {
    value: string,
    errorTexts?: Array<string>,
    onChange: Function,
  },
  companyName: {
    value: string,
    errorTexts?: Array<string>,
    onChange: Function,
  },
  companyNameKana: {
    value: string,
    errorTexts?: Array<string>,
    onChange: Function,
  },
  presidentName: {
    errorTexts?: Array<string>,
    onChange: Function,
  },
  presidentLastName: {
    value: string,
    onChange: Function,
  },
  presidentFirstName: {
    value: string,
    onChange: Function,
  },
  presidentNameKana: {
    errorTexts?: Array<string>,
  },
  presidentLastNameKana: {
    value: string,
    onChange: Function,
  },
  presidentFirstNameKana: {
    value: string,
    onChange: Function,
  },
  zipcode: {
    value: string,
    errorTexts?: Array<string>,
    onChange: Function,
  },
  stateList: Array<{
    name: string,
    name_text: string,
  }>,
  state: {
    value: string,
    errorTexts?: Array<string>,
    onChange: Function,
  },
  prefectures: {
    value: string,
    errorTexts?: Array<string>,
    onChange: Function,
  },
  street: {
    value: string,
    errorTexts?: Array<string>,
  },
  building: {
    value: string,
    errorTexts?: Array<string>,
    onChange: Function,
  },
  phoneNumber: {
    value: string,
    errorTexts?: Array<string>,
    onChange: Function,
  },
}

export default function PaidPaymentForm(props: PropTypes) {
  return (
    <FormList>
      <FormBlock>
        <Title>
          {i18n.t('page.dashboard.reservation.contactInformationTitle')}
        </Title>
        <BorderBoxInner>
          <FormBlockItem>
            <FormBlockItemHead>
              <FormSectionTitle
                title={i18n.t('page.shared.fullName')}
                required
                minimize
              />
            </FormBlockItemHead>
            <FormBlockItemDetail>
              <FormItemWrap>
                <FormItem
                  renderInput={() => (
                    <InputContainer>
                      <InputContainerInner>
                        <Input
                          size="large"
                          placeholder={i18n.t('page.shared.lastName')}
                          value={props.lastName.value}
                          onChange={e =>
                            props.lastName.onChange(e.target.value)
                          }
                        />
                        <Input
                          size="large"
                          placeholder={i18n.t('page.shared.firstName')}
                          value={props.firstName.value}
                          onChange={e =>
                            props.firstName.onChange(e.target.value)
                          }
                        />
                      </InputContainerInner>
                    </InputContainer>
                  )}
                  errorTexts={props.fullName.errorTexts}
                />
              </FormItemWrap>
            </FormBlockItemDetail>
          </FormBlockItem>
          <FormBlockItem>
            <FormBlockItemHead>
              <FormSectionTitle
                title={i18n.t('page.shared.fullNameKana')}
                required
                minimize
              />
            </FormBlockItemHead>
            <FormBlockItemDetail>
              <FormItemWrap>
                <FormItem
                  renderInput={() => (
                    <InputContainer>
                      <InputContainerInner>
                        <Input
                          size="large"
                          placeholder={i18n.t('page.shared.lastNameKana')}
                          value={props.lastNameKana.value}
                          onChange={e =>
                            props.lastNameKana.onChange(e.target.value)
                          }
                        />
                        <Input
                          size="large"
                          placeholder={i18n.t('page.shared.firstNameKana')}
                          value={props.firstNameKana.value}
                          onChange={e =>
                            props.firstNameKana.onChange(e.target.value)
                          }
                        />
                      </InputContainerInner>
                    </InputContainer>
                  )}
                  errorTexts={props.fullNameKana.errorTexts}
                />
              </FormItemWrap>
            </FormBlockItemDetail>
          </FormBlockItem>
        </BorderBoxInner>
      </FormBlock>
      <FormBlock>
        <Title>{i18n.t('page.shared.l_00063')}</Title>
        <BorderBoxInner>
          <FormBlockItem>
            <FormBlockItemHead>
              <FormSectionTitle
                title={i18n.t('page.dashboard.reservation.paidMailAddress')}
                description={i18n.t(
                  'page.dashboard.reservation.paidMailAddressAttentionText',
                )}
                required
                minimize
              />
            </FormBlockItemHead>
            <FormBlockItemDetail>
              <FormItem
                renderInput={() => (
                  <InputContainer>
                    <Input
                      size="large"
                      placeholder="sample@spacemarket.co.jp"
                      value={props.paidMailAddress.value}
                      onChange={e =>
                        props.paidMailAddress.onChange(e.target.value)
                      }
                    />
                  </InputContainer>
                )}
                errorTexts={props.paidMailAddress.errorTexts}
              />
            </FormBlockItemDetail>
          </FormBlockItem>
          <FormBlockItem>
            <FormBlockItemHead>
              <FormSectionTitle
                title={i18n.t('page.shared.companyName')}
                required
                minimize
              />
            </FormBlockItemHead>
            <FormBlockItemDetail>
              <FormItem
                renderInput={() => (
                  <InputContainer>
                    <Input
                      size="large"
                      placeholder={i18n.t(
                        'page.dashboard.reservation.paidFormplaceholderCompanyName',
                      )}
                      value={props.companyName.value}
                      onChange={e => props.companyName.onChange(e.target.value)}
                    />
                  </InputContainer>
                )}
                errorTexts={props.companyName.errorTexts}
              />
            </FormBlockItemDetail>
          </FormBlockItem>
          <FormBlockItem>
            <FormBlockItemHead>
              <FormSectionTitle
                title={i18n.t('page.shared.companyNameKana')}
                required
                minimize
              />
            </FormBlockItemHead>
            <FormBlockItemDetail>
              <FormItem
                renderInput={() => (
                  <InputContainer>
                    <Input
                      size="large"
                      placeholder={i18n.t(
                        'page.dashboard.reservation.paidFormplaceholderCompanyNameKana',
                      )}
                      value={props.companyNameKana.value}
                      onChange={e =>
                        props.companyNameKana.onChange(e.target.value)
                      }
                    />
                  </InputContainer>
                )}
                errorTexts={props.companyNameKana.errorTexts}
              />
            </FormBlockItemDetail>
          </FormBlockItem>
          <FormBlockItem>
            <FormBlockItemHead>
              <FormSectionTitle
                title={i18n.t('page.shared.presidentName')}
                required
                minimize
              />
            </FormBlockItemHead>
            <FormBlockItemDetail>
              <FormItemWrap>
                <FormItem
                  renderInput={() => (
                    <InputContainer>
                      <InputContainerInner>
                        <Input
                          size="large"
                          placeholder={i18n.t('page.shared.lastName')}
                          value={props.presidentLastName.value}
                          onChange={e =>
                            props.presidentLastName.onChange(e.target.value)
                          }
                        />
                        <Input
                          size="large"
                          placeholder={i18n.t('page.shared.firstName')}
                          value={props.presidentFirstName.value}
                          onChange={e =>
                            props.presidentFirstName.onChange(e.target.value)
                          }
                        />
                      </InputContainerInner>
                    </InputContainer>
                  )}
                  errorTexts={props.presidentName.errorTexts}
                />
              </FormItemWrap>
            </FormBlockItemDetail>
          </FormBlockItem>
          <FormBlockItem>
            <FormBlockItemHead>
              <FormSectionTitle
                title={i18n.t('page.shared.presidentNameKana')}
                required
                minimize
              />
            </FormBlockItemHead>
            <FormBlockItemDetail>
              <FormItemWrap>
                <FormItem
                  renderInput={() => (
                    <InputContainer>
                      <InputContainerInner>
                        <Input
                          size="large"
                          placeholder={i18n.t('page.shared.lastNameKana')}
                          value={props.presidentLastNameKana.value}
                          onChange={e =>
                            props.presidentLastNameKana.onChange(e.target.value)
                          }
                        />
                        <Input
                          size="large"
                          placeholder={i18n.t('page.shared.firstNameKana')}
                          value={props.presidentFirstNameKana.value}
                          onChange={e =>
                            props.presidentFirstNameKana.onChange(
                              e.target.value,
                            )
                          }
                        />
                      </InputContainerInner>
                    </InputContainer>
                  )}
                  errorTexts={props.presidentNameKana.errorTexts}
                />
              </FormItemWrap>
            </FormBlockItemDetail>
          </FormBlockItem>
          <FormBlockItem>
            <FormBlockItemHead>
              <FormSectionTitle
                title={i18n.t('page.shared.zipcode')}
                required
                minimize
              />
            </FormBlockItemHead>
            <FormBlockItemDetail>
              <FormItem
                renderInput={() => (
                  <InputContainer>
                    <Input
                      size="large"
                      placeholder="1600023"
                      value={props.zipcode.value}
                      onChange={e => props.zipcode.onChange(e.target.value)}
                    />
                  </InputContainer>
                )}
                errorTexts={props.zipcode.errorTexts}
              />
            </FormBlockItemDetail>
          </FormBlockItem>
          <FormBlockItem>
            <FormBlockItemHead>
              <FormSectionTitle
                title={i18n.t('page.shared.placeName')}
                required
                minimize
              />
            </FormBlockItemHead>
            <FormBlockItemDetail>
              <FormBlockItemDetailInner>
                <FormItemWrap>
                  <SelectItem
                    selectProps={{
                      size: 'large',
                      defaultValue: i18n.t('page.shared.l_00042'),
                      value: props.state.value,
                      onChange: v => props.state.onChange(v),
                    }}
                    optionList={props.stateList.map(s => ({
                      label: s.name_text,
                      value: s.name,
                    }))}
                  />
                </FormItemWrap>
                <FormItemWrap>
                  <FormItem
                    renderInput={() => (
                      <InputContainer>
                        <Input
                          size="large"
                          placeholder={i18n.t(
                            'page.dashboard.reservation.paidFormplaceholderPrefectures',
                          )}
                          value={props.prefectures.value}
                          onChange={e =>
                            props.prefectures.onChange(e.target.value)
                          }
                        />
                      </InputContainer>
                    )}
                    errorTexts={props.prefectures.errorTexts}
                  />
                </FormItemWrap>
              </FormBlockItemDetailInner>
            </FormBlockItemDetail>
          </FormBlockItem>
          <FormBlockItem>
            <FormBlockItemHead>
              <FormSectionTitle
                title={i18n.t('page.shared.streetName')}
                required
                minimize
              />
            </FormBlockItemHead>
            <FormBlockItemDetail>
              <FormItem
                renderInput={() => (
                  <InputContainer>
                    <Input
                      size="large"
                      placeholder={i18n.t(
                        'page.dashboard.reservation.paidFormplaceholderStreetName',
                      )}
                      value={props.street.value}
                      onChange={e => props.street.onChange(e.target.value)}
                    />
                  </InputContainer>
                )}
                errorTexts={props.street.errorTexts}
              />
            </FormBlockItemDetail>
          </FormBlockItem>
          <FormBlockItem>
            <FormBlockItemHead>
              <FormSectionTitle
                title={i18n.t('page.shared.buildingName')}
                required={false}
                minimize
              />
            </FormBlockItemHead>
            <FormBlockItemDetail>
              <FormItem
                renderInput={() => (
                  <InputContainer>
                    <Input
                      size="large"
                      placeholder={i18n.t(
                        'page.dashboard.reservation.paidFormplaceholderBuilding',
                      )}
                      value={props.building.value}
                      onChange={e => props.building.onChange(e.target.value)}
                    />
                  </InputContainer>
                )}
                errorTexts={props.building.errorTexts}
              />
            </FormBlockItemDetail>
          </FormBlockItem>
          <FormBlockItem>
            <FormBlockItemHead>
              <FormSectionTitle
                title={i18n.t('page.shared.phoneNumber')}
                description={i18n.t(
                  'page.dashboard.reservation.phoneNumberAttentionText',
                )}
                required
                minimize
              />
            </FormBlockItemHead>
            <FormBlockItemDetail>
              <FormItem
                renderInput={() => (
                  <InputContainer>
                    <Input
                      size="large"
                      placeholder={i18n.t(
                        'page.dashboard.reservation.paidFormplaceholderPhoneNumber',
                      )}
                      value={props.phoneNumber.value}
                      onChange={e => props.phoneNumber.onChange(e.target.value)}
                    />
                  </InputContainer>
                )}
                errorTexts={props.phoneNumber.errorTexts}
              />
            </FormBlockItemDetail>
          </FormBlockItem>
        </BorderBoxInner>
      </FormBlock>
    </FormList>
  )
}
const FormList = styled.div`
  margin-top: ${spacing.large}px;
  ${mediaMin.phone`	
    margin-top: ${spacing.huge}px;
  `};
`
const Title = styled.h2`
  font-size: ${fontSize.medium}px;
  font-weight: bold;
  padding-bottom: ${spacing.small_3}px;
  border-bottom: 1px solid ${Colors.grayE};
  ${mediaMin.phone`	
    font-size: ${fontSize.medium_2}px;
    font-weight: normal;
    padding-bottom: 18px;
  `};
`
const FormBlock = styled.div`
  &:not(:first-child) {
    margin-top: ${spacing.large}px;
    ${mediaMin.phone`	
      margin-top: ${spacing.huge}px;
    `};
  }
`
const BorderBoxInner = styled.div`
  margin-top: ${spacing.medium_2}px;
  ${mediaMin.phone`	
    margin-top: ${spacing.large}px;
  `};
`
const InputContainerInner = styled.div`
  display: flex;
  input {
    &:not(:first-child) {
      margin-left: ${spacing.small_2}px;
    }
  }
`
const FormBlockItem = styled.div`
  &:not(:first-child) {
    margin-top: ${spacing.large}px;
    ${mediaMin.phone`	
    margin-top: ${spacing.large}px;
  `};
  }
`
const FormBlockItemHead = styled.dt`
  font-size: ${fontSize.small_2}px;
  ${mediaMin.phone`	
    font-size: ${fontSize.small_3}px;
  `};
`
const FormBlockItemDetail = styled.dd`
  margin-top: 20px;
  ${mediaMin.phone`	
    margin-top: ${spacing.medium_2}px;
    max-width: 320px;
  `};
`
const FormBlockItemDetailInner = styled.div`
  display: flex;
  & > * {
    width: 50%;
  }
`
const FormItemWrap = styled.div`
  &:not(:first-child) {
    margin-left: ${spacing.small_2}px;
  }
`



// WEBPACK FOOTER //
// ./src/components/organisms/PaidPaymentForm/index.jsx